import { createStore, applyMiddleware, compose } from 'redux';
import { routerMiddleware } from 'connected-react-router';
import { withExtraArgument } from 'redux-thunk';
import { createBrowserHistory } from 'history';
import createRootReducer from 'reducers';
import SocketDispatcher from './SocketDispatcher/SocketDispatcher';

import knockManager from './subscribers/knockManager';
import attendeeDetailsManager from './subscribers/attendeeDetails/attendeeDetailsManager';
import {
  api as apiMiddleware,
  videoPriorityBasedPolicyMiddleware,
  streamRecordStatus
} from '@shoflo/studio-redux/middleware';
import startSurveyOnStreamEnd from '../middleware/startSurveyOnStreamEnd';

/**
 * We are allowing passing of browser history to allow us to write stories for
 * components that trigger actions that rely on browser history. Ideally the
 * stories will use "createMemoryHistory" to create the history.
 *
 * @param history
 * @param initialState
 */
function createConfiguredStore(history = null, initialState = {}) {
  const socketDispatcher = new SocketDispatcher();
  const socket = socketDispatcher.connect();

  history = history || createBrowserHistory();

  const enhancers = [];
  const middleware = [
    withExtraArgument(socket),
    routerMiddleware(history),
    apiMiddleware,
    startSurveyOnStreamEnd,
    videoPriorityBasedPolicyMiddleware,
    streamRecordStatus(socket)
  ];

  if (process.env.NODE_ENV === 'development') {
    const devToolsExtension = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__;

    if (typeof devToolsExtension === 'function') {
      enhancers.push(devToolsExtension());
    }
  }

  const composedEnhancers = compose(
    applyMiddleware(...middleware),
    ...enhancers
  );

  const store = createStore(
    createRootReducer(history),
    initialState,
    composedEnhancers
  );

  socketDispatcher.bindToStore(store);

  store.subscribe(knockManager.bind({ store, socket }));
  store.subscribe(attendeeDetailsManager.bind({ store, socket }));

  return {
    store,
    history
  };
}

export { createConfiguredStore };
